'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import klaroConfig from '@/lib/klaro-config';

type Consents = Record<string, boolean>;

type ConsentContextValue = {
  consents: Consents;
  hasConsent: (serviceName: string) => boolean;
  showConsentModal: () => void;
  ready: boolean;
  /** Called by authenticated code to override consent for logged-in users */
  setUserConsent: (analyticsGranted: boolean) => void;
};

const ConsentContext = createContext<ConsentContextValue>({
  consents: {},
  hasConsent: () => false,
  showConsentModal: () => {},
  ready: false,
  setUserConsent: () => {},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type KlaroGlobal = { getManager: any; render: any; show: any };

function getKlaro(): KlaroGlobal | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).klaro ?? null;
}

function loadKlaroScript(): Promise<KlaroGlobal> {
  return new Promise((resolve, reject) => {
    // Already loaded
    const existing = getKlaro();
    if (existing) {
      resolve(existing);
      return;
    }

    // Load CSS
    if (!document.getElementById('klaro-css')) {
      const link = document.createElement('link');
      link.id = 'klaro-css';
      link.rel = 'stylesheet';
      link.href = '/scripts/klaro.css';
      document.head.appendChild(link);
    }

    // Load JS
    const script = document.createElement('script');
    script.src = '/scripts/klaro.js';
    script.async = true;
    // klaro.js bundles core-js, which replaces GLOBAL Function.prototype.toString
    // with a WeakMap-backed shim. Every fn.toString() on the whole page then pays
    // that cost — React 19's dev prop-differ calls it per function prop per commit,
    // which amplified the 2026-07-16 company-detail freeze. The patch is cosmetic
    // (it only makes core-js's own polyfills print as native code), so restore the
    // native implementation once klaro has finished loading.
    const nativeFunctionToString = Function.prototype.toString;
    script.onload = () => {
      if (Function.prototype.toString !== nativeFunctionToString) {
        Function.prototype.toString = nativeFunctionToString;
      }
      const klaro = getKlaro();
      if (klaro) {
        resolve(klaro);
      } else {
        reject(new Error('Klaro script loaded but window.klaro not available'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Klaro script'));
    document.head.appendChild(script);
  });
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consents, setConsents] = useState<Consents>({});
  const [ready, setReady] = useState(false);
  const [userConsentApplied, setUserConsentApplied] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    loadKlaroScript()
      .then((klaro) => {
        const manager = klaro.getManager(klaroConfig);

        // Read initial consent state
        const initial: Consents = {};
        for (const service of klaroConfig.services) {
          initial[service.name] = manager.getConsent(service.name) ?? service.required ?? false;
        }
        setConsents(initial);
        setReady(true);

        // Watch for consent changes
        manager.watch({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          update: (_manager: any, eventType: string) => {
            if (eventType === 'saveConsents' || eventType === 'consents') {
              const updated: Consents = {};
              for (const service of klaroConfig.services) {
                updated[service.name] = manager.getConsent(service.name) ?? service.required ?? false;
              }
              setConsents(updated);
            }
          },
        });

        // Render the consent modal UI
        klaro.render(klaroConfig);
      })
      .catch((err) => {
        console.error('Failed to load Klaro consent manager:', err);
        // Fallback: treat everything as consented so the site doesn't break
        const fallback: Consents = {};
        for (const service of klaroConfig.services) {
          fallback[service.name] = true;
        }
        setConsents(fallback);
        setReady(true);
      });
  }, []);

  // When a logged-in user has accepted or declined analytics consent at registration,
  // override the consent state and hide the Klaro banner.
  const setUserConsent = useCallback((analyticsGranted: boolean) => {
    setUserConsentApplied(true);
    setConsents((prev) => ({
      ...prev,
      googleAnalytics: analyticsGranted,
      maps: analyticsGranted,
    }));

    // If user declined, delete any existing GA cookies
    if (!analyticsGranted) {
      for (const cookie of document.cookie.split(';')) {
        const name = cookie.split('=')[0].trim();
        if (/^_ga|^_gid|^_gat/.test(name)) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
        }
      }
    }

    // Hide the Klaro banner since the user already made their choice
    const klaroEl = document.querySelector('.klaro');
    if (klaroEl) {
      (klaroEl as HTMLElement).style.display = 'none';
    }
  }, []);

  const hasConsent = useCallback(
    (serviceName: string) => {
      const service = klaroConfig.services.find((s) => s.name === serviceName);
      if (service?.required) return true;
      if (userConsentApplied && (serviceName === 'googleAnalytics' || serviceName === 'maps')) {
        return consents[serviceName] ?? false;
      }
      return consents[serviceName] ?? false;
    },
    [consents, userConsentApplied],
  );

  const showConsentModal = useCallback(() => {
    const klaro = getKlaro();
    if (klaro) {
      klaro.show(klaroConfig);
    }
  }, []);

  return (
    <ConsentContext.Provider value={{ consents, hasConsent, showConsentModal, ready, setUserConsent }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(serviceName?: string) {
  const ctx = useContext(ConsentContext);
  const granted = serviceName ? ctx.hasConsent(serviceName) : true;
  return {
    granted,
    consents: ctx.consents,
    showConsentModal: ctx.showConsentModal,
    setUserConsent: ctx.setUserConsent,
    ready: ctx.ready,
  };
}
